import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Heart, MessageCircle, ArrowUpRight } from "lucide-react";
import { ViewerMedia } from "@/components/feed/ViewerMedia";
import { compact } from "@/lib/feed";
import { TrendingBadge } from "@/components/feed/TrendingBadge";
import { ShareButton } from "@/components/feed/ShareButton";

const NavBtn = ({ dir, onClick, disabled }) => (
  <button
    data-testid={`viewer-${dir}-btn`}
    onClick={onClick}
    disabled={disabled}
    aria-label={dir === "prev" ? "Previous post" : "Next post"}
    className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--line)] transition-colors duration-300 hover:border-[var(--volt)] hover:text-[var(--volt)] disabled:opacity-25 disabled:pointer-events-none"
  >
    {dir === "prev" ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </button>
);

export const PostViewer = ({ posts, index, onChange, trending }) => {
  const post = index === null ? null : posts[index];
  const [fromEnd, setFromEnd] = useState(false);
  const go = (d, end = false) => {
    setFromEnd(end);
    onChange(Math.max(0, Math.min(posts.length - 1, index + d)));
  };

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.target.tagName === "VIDEO") return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onChange(null)}>
      {post && (
        <DialogContent data-testid="post-viewer" className="max-w-6xl w-[95vw] h-[90vh] p-0 gap-0 border-[var(--line)] bg-[var(--ink-2)] text-[var(--bone)] rounded-none sm:rounded-none overflow-hidden grid-cols-1 grid-rows-[50vh_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)] md:grid-cols-[1fr_380px] [&>button:last-child]:hidden">
          <ViewerMedia key={post.id} post={post} startAtEnd={fromEnd} onEdge={(d) => go(d, d < 0)} />
          <div className="flex flex-col min-h-0 border-t md:border-t-0 md:border-l border-[var(--line)]">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--line)]">
              <span data-testid="viewer-counter" className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ash)]">
                {String(index + 1).padStart(2, "0")} / {String(posts.length).padStart(2, "0")}
              </span>
              <button data-testid="viewer-close-btn" onClick={() => onChange(null)} aria-label="Close" className="text-[var(--ash)] transition-colors hover:text-[var(--bone)]">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {trending.has(post.id) && <TrendingBadge testid="viewer-trending-badge" className="mb-4" />}
              <DialogTitle className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--volt)] font-normal">
                {new Date(post.posted_at).toLocaleDateString("en", { month: "long", day: "numeric", year: "numeric" })}
              </DialogTitle>
              <DialogDescription data-testid="viewer-caption" className="mt-4 whitespace-pre-line text-[15px] leading-relaxed text-[var(--bone)]">
                {post.caption || "No caption."}
              </DialogDescription>
            </div>
            <div className="px-6 py-5 border-t border-[var(--line)] space-y-5">
              <div className="flex items-center gap-5 font-mono text-xs text-[var(--ash)]">
                <span data-testid="viewer-likes" className="flex items-center gap-1.5"><Heart className="h-3.5 w-3.5" />{compact(post.likes)}</span>
                <span data-testid="viewer-comments" className="flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" />{compact(post.comments)}</span>
                <a data-testid="viewer-open-instagram" href={post.url} target="_blank" rel="noreferrer" className="ml-auto flex items-center gap-1 text-[var(--bone)] hover:text-[var(--volt)] transition-colors">
                  Instagram <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
              <div className="flex gap-3">
                <NavBtn dir="prev" onClick={() => go(-1)} disabled={index === 0} />
                <NavBtn dir="next" onClick={() => go(1)} disabled={index === posts.length - 1} />
                <ShareButton shortcode={post.shortcode} />
              </div>
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};
